import { z } from "zod";
import { NOTICE_CATEGORIES, type NoticeCategory } from "../notifications/feed";

/**
 * Preferências do usuário — pares chave/valor por dono.
 *
 * O formato é deliberadamente genérico (`jsonb`) porque cada chave tem um
 * formato próprio e novas preferências não deveriam exigir uma migration de
 * coluna. O preço disso é que **ler exige validar**: um valor gravado por uma
 * versão anterior do app pode não ter mais o formato esperado, e tratar isso
 * como `any` espalharia o erro pela interface.
 *
 * A regra de leitura é fail-closed com fallback declarado: valor ausente ou
 * inválido devolve o padrão conhecido e sinaliza que houve descarte, em vez de
 * deixar a tela meio configurada sem explicação (§8).
 *
 * O tema claro/escuro NÃO mora aqui: ele é preferência de dispositivo e vive no
 * `localStorage` do navegador, senão trocar de máquina forçaria o tema da outra.
 */

export interface UserPreference {
  key: string;
  value: unknown;
  updatedAt: string;
  version: number;
}

/** Chaves conhecidas; uma chave fora desta lista não é gravada. */
export const PREFERENCE_KEYS = ["notifications", "profile", "ai", "portal", "leadOptions"] as const;
export type PreferenceKey = (typeof PREFERENCE_KEYS)[number];

const categoryFlagsSchema = z
  .object(
    Object.fromEntries(
      NOTICE_CATEGORIES.map((category) => [category, z.boolean()]),
    ) as Record<NoticeCategory, z.ZodBoolean>,
  )
  .partial();

export const notificationPreferencesSchema = z.object({
  /** Categorias exibidas no sino e na central; ausente significa exibida. */
  categories: categoryFlagsSchema.default({}),
  /** Categorias que também abrem aviso flutuante ao chegar. */
  banners: categoryFlagsSchema.default({}),
  /** Canto em que o aviso flutuante aparece. */
  bannerPosition: z
    .enum(["top-right", "top-left", "bottom-right", "bottom-left", "bottom-center"])
    .default("bottom-right"),
});

export type NotificationPreferences = z.output<typeof notificationPreferencesSchema>;

export const profilePreferencesSchema = z.object({
  /** Como a pessoa se identifica no espaço de trabalho. */
  displayName: z.string().trim().max(120).default(""),
  agencyName: z.string().trim().max(120).default(""),
  timeZone: z.string().trim().max(60).default("America/Sao_Paulo"),
});

export type ProfilePreferences = z.output<typeof profilePreferencesSchema>;

/**
 * Orientação de escrita para as sugestões.
 *
 * É preferência, não segredo: nenhuma chave, endpoint ou credencial entra
 * aqui (§26.1). O texto só orienta o tom; guardá-lo não liga provedor nenhum.
 */
export const aiPreferencesSchema = z.object({
  instructions: z.string().trim().max(2000).default(""),
});

export type AiPreferences = z.output<typeof aiPreferencesSchema>;

/**
 * Estado demonstrativo do acesso do cliente ao portal, por lead.
 *
 * NÃO é conta, convite nem autenticação: o portal não tem acesso externo (§9).
 * O que se guarda é só a etapa da demonstração que o operador já percorreu, e
 * a tela diz isso em cada passo. Mora em preferência, e não em tabela própria,
 * justamente porque não é fato do domínio — é estado da prévia.
 */
export const portalPreferencesSchema = z.object({
  access: z.record(z.string(), z.enum(["invited", "active"])).default({}),
});

export type PortalPreferences = z.output<typeof portalPreferencesSchema>;

/**
 * Sugestões de Origem e Segmento no cadastro do lead.
 *
 * O lead continua guardando texto livre; isto é só a lista que a pessoa cura
 * em Configurações — o que ela adicionou e o que apagou de propósito. A regra
 * que junta isso aos valores em uso mora em `features/leads/lead-options.ts`.
 */
const optionListSchema = z.array(z.string().trim().min(1).max(60)).max(200).default([]);

export const leadOptionsPreferencesSchema = z.object({
  sources: optionListSchema,
  segments: optionListSchema,
  removedSources: optionListSchema,
  removedSegments: optionListSchema,
});

export type LeadOptionsPreferences = z.output<typeof leadOptionsPreferencesSchema>;

export const PREFERENCE_SCHEMAS = {
  notifications: notificationPreferencesSchema,
  profile: profilePreferencesSchema,
  ai: aiPreferencesSchema,
  portal: portalPreferencesSchema,
  leadOptions: leadOptionsPreferencesSchema,
} as const satisfies Record<PreferenceKey, z.ZodTypeAny>;

/** Formato de cada chave, para a leitura devolver o tipo certo sem `any`. */
export interface PreferenceValueMap {
  notifications: NotificationPreferences;
  profile: ProfilePreferences;
  ai: AiPreferences;
  portal: PortalPreferences;
  leadOptions: LeadOptionsPreferences;
}

export function isPreferenceKey(value: unknown): value is PreferenceKey {
  return typeof value === "string" && (PREFERENCE_KEYS as readonly string[]).includes(value);
}

export interface PreferenceRead<T> {
  value: T;
  /** Verdadeiro quando o valor gravado foi descartado por não validar. */
  fellBack: boolean;
}

/**
 * Lê uma preferência aplicando o schema da chave.
 *
 * Nunca lança: a interface precisa de um valor utilizável mesmo diante de um
 * registro antigo. `fellBack` existe para a tela poder avisar que a preferência
 * gravada foi ignorada, em vez de silenciosamente parecer redefinida.
 */
export function readPreference(
  preferences: readonly UserPreference[],
  key: "notifications",
): PreferenceRead<NotificationPreferences>;
export function readPreference(
  preferences: readonly UserPreference[],
  key: "profile",
): PreferenceRead<ProfilePreferences>;
export function readPreference(
  preferences: readonly UserPreference[],
  key: "ai",
): PreferenceRead<AiPreferences>;
export function readPreference(
  preferences: readonly UserPreference[],
  key: "portal",
): PreferenceRead<PortalPreferences>;
export function readPreference(
  preferences: readonly UserPreference[],
  key: "leadOptions",
): PreferenceRead<LeadOptionsPreferences>;
export function readPreference(
  preferences: readonly UserPreference[],
  key: PreferenceKey,
): PreferenceRead<PreferenceValueMap[PreferenceKey]> {
  const schema = PREFERENCE_SCHEMAS[key];
  const stored = preferences.find((item) => item.key === key);
  const parsed = schema.safeParse(stored?.value ?? {});
  if (parsed.success) {
    return { value: parsed.data, fellBack: stored !== undefined && !isPlainObject(stored.value) };
  }
  // O valor gravado não vale mais: devolve o padrão da própria chave.
  return { value: schema.parse({}), fellBack: true };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Categorias silenciadas, no formato que o feed consome. */
export function mutedCategories(preferences: NotificationPreferences): NoticeCategory[] {
  return NOTICE_CATEGORIES.filter((category) => preferences.categories[category] === false);
}
